<script setup>
import {
  ref,
  computed,
  defineAsyncComponent,
  defineProps,
  defineEmits,
  defineExpose,
} from "vue";
import OrderItem from "./OrderItem.vue";
import { useOrdersStore } from "@/store/orders";
import { debounce } from "lodash-es";
import {storeToRefs} from "pinia";

// Добавляем пропс для состояния фильтров
const props = defineProps({
  filtersOpen: {
    type: Boolean,
    default: false,
  },
});

const LoadingSpinner = defineAsyncComponent(() =>
  import("@/components/LoadingSpinner.vue")
);
const ordersStore = useOrdersStore();
const { ordersWithRoute, loading, ordersCount } = storeToRefs(ordersStore);
const items = ordersWithRoute;   // alias

const pageSize = 20;
const currentPage = ref(1);
const containerRef = ref(null);
const count = ordersCount;
const resetPage = () => { currentPage.value = 1; };

// Вычисляем высоту контейнера в зависимости от состояния фильтров
const containerHeight = computed(() => {
  return props.filtersOpen ? "calc(100vh - 345px)" : "calc(100vh - 200px)";
});

// Упрощаем логику - отказываемся от виртуализации в пользу пагинации
const paginatedItems = computed(() => {
  const end = currentPage.value * pageSize;
  return items.value.slice(0, end);
});

const hasMoreItems = computed(
  () => paginatedItems.value.length < items.value.length
);

const loadMoreItems = debounce(() => {
  if (!hasMoreItems.value) return;
  currentPage.value++;
}, 200);

const onScroll = () => {
  if (!containerRef.value) return;

  // Проверяем, достигли ли мы конца списка
  const { scrollTop, scrollHeight, clientHeight } = containerRef.value;

  // Загружаем больше элементов, когда пользователь прокрутил почти до конца
  if (scrollTop + clientHeight >= scrollHeight - 100 && hasMoreItems.value) {
    loadMoreItems();
  }
};

const emit = defineEmits(["show-more"]);

const showMore = (item) => {
  emit("show-more", item);
};

defineExpose({
  count,
  resetPage
});
</script>

<template>
  <div
    class="order-list-container"
    ref="containerRef"
    @scroll="onScroll"
    :style="{ height: containerHeight }"
  >
    <div v-if="loading && paginatedItems.length === 0" class="spinner-container">
      <LoadingSpinner />
    </div>
    <div
      v-else-if="paginatedItems.length === 0 && !loading"
      class="no-orders"
    >
      <span>Заказов не найдено</span>
    </div>
    <div v-else class="order-list">
      <OrderItem
        v-for="item in paginatedItems"
        :key="item.id"
        :order="item"
        @show-more="showMore"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.order-list-container {
  width: 100%;
  overflow-y: auto;
  box-sizing: border-box;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
    background-color: #f1f1f1;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
    min-height: 40px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
}

.order-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 40px;
}

.no-orders {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;

  span {
    font-size: 14px;
    font-weight: 600;
    font-family: Open Sans, sans-serif;
    // color: var(--text-color);
    color: #4d5460;
  }
}

.spinner-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 200px;
  text-align: center;

  span {
    font-size: 14px;
    font-weight: 500;
    // color: var(--text-color);
    color: #4d5460;
  }
}
</style>
